import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { TenantUsersDto } from '@buspro/web/apis';
import { NgxAuthService } from '@corelabs/ng-auth';

interface SettingsSidebarItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  description?: string;
}

@Component({
  selector: 'tenant-settings-page',
  standalone: true,
  imports: [NgClass, NgIf, NgFor, RouterLink, RouterLinkActive, RouterOutlet],
  template: `
    <div class="settings-container">
      <!-- Settings Header -->
      <div class="settings-header">
        <div class="settings-header-content">
          <div>
            <h1 class="settings-title">Settings</h1>
            <p class="settings-subtitle">Manage your company settings and preferences</p>
          </div>
        </div>
      </div>

      <!-- Settings Content -->
      <div class="settings-content">
        <!-- Sidebar -->
        <aside class="settings-sidebar">
          <nav class="settings-nav">
            <div class="settings-nav-section">
              <h3 class="settings-nav-heading">General</h3>
              <ul class="settings-nav-list">
                <li *ngFor="let item of generalSettings">
                  <a
                    [routerLink]="item.route"
                    routerLinkActive="active"
                    class="settings-nav-item"
                  >
                    <span [class]="'fe ' + item.icon + ' settings-nav-icon'"></span>
                    <div class="settings-nav-text">
                      <span class="settings-nav-label">{{ item.label }}</span>
                      <span class="settings-nav-description" *ngIf="item.description">{{ item.description }}</span>
                    </div>
                  </a>
                </li>
              </ul>
            </div>

            <div class="settings-nav-section">
              <h3 class="settings-nav-heading">Communication</h3>
              <ul class="settings-nav-list">
                <li *ngFor="let item of communicationSettings">
                  <a
                    [routerLink]="item.route"
                    routerLinkActive="active"
                    class="settings-nav-item"
                  >
                    <span [class]="'fe ' + item.icon + ' settings-nav-icon'"></span>
                    <div class="settings-nav-text">
                      <span class="settings-nav-label">{{ item.label }}</span>
                      <span class="settings-nav-description" *ngIf="item.description">{{ item.description }}</span>
                    </div>
                  </a>
                </li>
              </ul>
            </div>

            <div class="settings-nav-section">
              <h3 class="settings-nav-heading">Operations</h3>
              <ul class="settings-nav-list">
                <li *ngFor="let item of operationsSettings">
                  <a
                    [routerLink]="item.route"
                    routerLinkActive="active"
                    class="settings-nav-item"
                  >
                    <span [class]="'fe ' + item.icon + ' settings-nav-icon'"></span>
                    <div class="settings-nav-text">
                      <span class="settings-nav-label">{{ item.label }}</span>
                      <span class="settings-nav-description" *ngIf="item.description">{{ item.description }}</span>
                    </div>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </aside>

        <!-- Main Content -->
        <main class="settings-main">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `,
  styles: [
    `
    .settings-container {
      min-height: 100vh;
      background-color: var(--content-bg);
    }

    .settings-header {
      background-color: var(--header-bg);
      border-bottom: 1px solid var(--header-border);
      padding: 24px 32px;
    }

    .settings-header-content {
      max-width: 1400px;
      margin: 0 auto;
    }

    .settings-title {
      font-size: 28px;
      font-weight: 600;
      color: var(--header-text);
      margin: 0 0 4px 0;
    }

    .settings-subtitle {
      font-size: 14px;
      color: var(--header-text-secondary);
      margin: 0;
    }

    .settings-content {
      max-width: 1400px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 32px;
      padding: 32px;
      min-height: calc(100vh - 140px);
    }

    .settings-sidebar {
      position: sticky;
      top: 32px;
      height: fit-content;
    }

    .settings-nav {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .settings-nav-section {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .settings-nav-heading {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--card-text-secondary);
      margin: 0 0 8px 0;
      padding: 0 12px;
    }

    .settings-nav-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .settings-nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 10px 12px;
      border-radius: 8px;
      text-decoration: none;
      color: var(--card-text);
      transition: all var(--transition-fast);
      cursor: pointer;

      &:hover {
        background-color: var(--card-hover);
        color: var(--accent-primary);

        .settings-nav-icon {
          color: var(--accent-primary);
        }
      }

      &.active {
        background-color: rgba(0, 114, 188, 0.08);
        color: var(--accent-primary);
        border-left: 3px solid var(--accent-primary);
        padding-left: 9px;

        .settings-nav-icon {
          color: var(--accent-primary);
        }
      }
    }

    .settings-nav-icon {
      font-size: 18px;
      color: var(--card-text-secondary);
      flex-shrink: 0;
    }

    .settings-nav-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .settings-nav-label {
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .settings-nav-description {
      font-size: 12px;
      color: var(--card-text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .settings-main {
      background-color: var(--header-bg);
      border: 1px solid var(--header-border);
      border-radius: 12px;
      padding: 32px;
      min-height: 600px;
    }

    @media (max-width: 1024px) {
      .settings-content {
        grid-template-columns: 1fr;
        gap: 24px;
        padding: 16px;
      }

      .settings-sidebar {
        position: static;
      }

      .settings-header {
        padding: 16px 24px;
      }

      .settings-title {
        font-size: 24px;
      }
    }
  `,
  ],
})
export class SettingsPageComponent {
  generalSettings: SettingsSidebarItem[] = [
    {
      id: 'company',
      label: 'Company',
      icon: 'fe-briefcase',
      route: '/settings/company',
      description: 'Manage company details',
    },
  ];

  communicationSettings: SettingsSidebarItem[] = [
    {
      id: 'sms-templates',
      label: 'SMS Templates',
      icon: 'fe-message-square',
      route: '/settings/sms-templates',
      description: 'Configure SMS messages',
    },
  ];

  operationsSettings: SettingsSidebarItem[] = [
    {
      id: 'discounts',
      label: 'Discount Rates',
      icon: 'fe-percent',
      route: '/settings/discounts',
      description: 'Manage pricing discounts',
    },
    {
      id: 'parcel-item-types',
      label: 'Parcel Item Types',
      icon: 'fe-package',
      route: '/settings/parcel-item-types',
      description: 'Configure parcel categories',
    },
    {
      id: 'cash',
      label: 'Cash Handovers',
      icon: 'fe-dollar-sign',
      route: '/settings/cash',
      description: 'Grace period before cash goes short',
    },
  ];
}
